<template>
  <s-loader :value="loading"/>
  <tinymce
    v-if="!showTextarea"
    :class="{ 'd-none': !showEditor }"
    v-model="modelValue"
    :init="config"
    :disabled="props.readonly"
  />

  <s-textarea
    v-if="showTextarea"
    class="form-control overflow-hidden"
    v-model="modelValue"
  />
</template>

<script setup>
  import { ref, reactive, computed, defineAsyncComponent, onMounted } from 'vue';
  import 'tinymce';

  /* Required TinyMCE components */
  import 'tinymce/icons/default/icons.min.js';
  import 'tinymce/models/dom/model.min.js';
  import 'tinymce/themes/silver/theme.min.js';

  /* Import plugins */
  import 'tinymce/plugins/autoresize';
  import 'tinymce/plugins/image';

  /* content UI CSS is required */
  import 'tinymce/skins/ui/oxide/content.js';
  import 'tinymce/skins/content/default/content.js';

  const Tinymce = defineAsyncComponent(() => new Promise((resolve) => {
    // render default textarea if promise allSettled doesn't exist
    // e.g = chrome < 76
    if (Promise.allSettled) {
      resolve(import('@tinymce/tinymce-vue'));
    } else {
      showTextarea.value = true;
    }
  }));

  const emit = defineEmits(['update:modelValue']);

  const props = defineProps({
    modelValue: {
      required: true,
      type: null,
    },
    plugins: {
      type: Array,
      default: () => [],
    },
    extendedValidElements: {
      type: String,
      default: '',
    },
    toolbar: {
      type: String,
      default: '',
    },
    imageUploadHandler: {
      type: Function,
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    maxHeight: {
      type: Number,
      default: null,
    },
    newLineBehavior: {
      type: String,
      default: 'default',
    },
  });

  const showEditor = ref(false);
  const loading = ref(false);
  // fallback if tinymce cannot be rendered
  const showTextarea = ref(false);

  const config = reactive({
    skin: false,
    plugins: ['autoresize', ...props.plugins],
    valid_elements: 'b,i,u,ul,li,p,br',
    extended_valid_elements: props.extendedValidElements,
    height: 200,
    menubar: false,
    toolbar:
      `undo redo | bold italic underline bullist ${props.toolbar}`,
    formats: {
      bold: { inline: 'b' },
      italic: { inline: 'i' },
      underline: { inline: 'u' },
    },
    branding: false,
    paste_block_drop: true,
    max_height: props.maxHeight,
    newline_behavior: props.newLineBehavior,
    setup() {
      loading.value = false;
    },
    init_instance_callback: () => {
      showEditor.value = true;
    },
    images_upload_handler: props.imageUploadHandler,
    license_key: 'gpl',
  });

  const modelValue = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
  });

  onMounted(() => {
    if (Promise.allSettled) {
      // check if tinymce was initialized globally first time
      loading.value = !window.tinymce;
    } else {
      loading.value = false;
    }
  });

  function insertText(text) {
    window.tinymce?.activeEditor.execCommand('mceInsertContent', false, `[[${text}]]`);
  }

  defineExpose({ insertText });
</script>
